import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Send, Trash2 } from "lucide-react";
import { AdminShell } from "../components/admin/AdminShell";
import { API } from "../lib/eventUtils";

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—");

export default function AdminSubscribers() {
  const [data, setData] = useState(null);
  const [digests, setDigests] = useState([]);
  const [error, setError] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const [linkLabel, setLinkLabel] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [subs, digs] = await Promise.all([
        axios.get(`${API}/api/admin/subscribers`, { withCredentials: true }),
        axios.get(`${API}/api/admin/digests`, { withCredentials: true }),
      ]);
      setData(subs.data);
      setDigests(digs.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Could not load subscribers");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const sendAnnouncement = async () => {
    setBusy(true);
    try {
      const { data: res } = await axios.post(
        `${API}/api/admin/announce`,
        { subject, message, link_url: linkUrl || null, link_label: linkLabel || null },
        { withCredentials: true }
      );
      toast.success(`Announcement queued to ${res.recipients} subscriber${res.recipients === 1 ? "" : "s"}`);
      setSubject(""); setMessage(""); setLinkUrl(""); setLinkLabel(""); setConfirming(false);
      setTimeout(load, 2500);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
      setConfirming(false);
    } finally {
      setBusy(false);
    }
  };

  const removeSubscriber = async (email) => {
    try {
      await axios.delete(`${API}/api/admin/subscribers/${encodeURIComponent(email)}`, { withCredentials: true });
      toast.success("Subscriber removed");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Remove failed");
    }
  };

  return (
    <AdminShell title="Subscribers & Digest">
      <div data-testid="admin-subscribers-page">
        {error && <p className="border border-red-500/40 bg-red-500/10 text-red-300 text-sm px-4 py-3 mb-6" data-testid="subscribers-error">{error}</p>}
        {!data && !error && <p className="font-mono2 text-xs uppercase tracking-[0.25em] text-zinc-500 animate-pulse" data-testid="subscribers-loading">Loading…</p>}
        {data && (
          <div className="space-y-8">
            <div className="border border-white/10 bg-[#0c0c0c] p-6" data-testid="subscribers-auto-card">
              <p className="eyebrow mb-2 flex items-center gap-2"><Mail size={13} /> Automatic Digest</p>
              <p className="text-zinc-400 text-sm leading-relaxed">
                Publishing a new event from the Events tab automatically emails all{" "}
                <span className="text-volt font-semibold" data-testid="subscribers-count">{data.count}</span>{" "}
                blog subscriber{data.count === 1 ? "" : "s"} with the date, price and booking link (sent once per event).
                Use the composer below for everything else — recap drops, schedule changes, announcements.
              </p>
            </div>

            <div className="border border-volt/40 bg-[#0c0c0c] p-6" data-testid="announce-composer-card">
              <p className="eyebrow mb-4 flex items-center gap-2"><Send size={13} /> Send an Announcement</p>
              <div className="space-y-4 max-w-2xl">
                <label className="block">
                  <span className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-zinc-400 block mb-1">Subject</span>
                  <input value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={120} placeholder="The Oct 25 sim recap is live" className="volt-input" data-testid="announce-subject-input" />
                </label>
                <label className="block">
                  <span className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-zinc-400 block mb-1">Message</span>
                  <textarea rows={5} value={message} onChange={(e) => setMessage(e.target.value)} maxLength={5000} placeholder="Plain text. Line breaks are kept." className="volt-input" data-testid="announce-message-input" />
                </label>
                <div className="grid sm:grid-cols-2 gap-4">
                  <label className="block">
                    <span className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-zinc-400 block mb-1">Button link (optional, https)</span>
                    <input value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)} placeholder="https://hyroxninja.com/blog/..." className="volt-input" data-testid="announce-link-input" />
                  </label>
                  <label className="block">
                    <span className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-zinc-400 block mb-1">Button label</span>
                    <input value={linkLabel} onChange={(e) => setLinkLabel(e.target.value)} placeholder="Read the Recap" className="volt-input" data-testid="announce-label-input" />
                  </label>
                </div>
                {!confirming ? (
                  <button
                    onClick={() => setConfirming(true)}
                    disabled={!subject.trim() || message.trim().length < 10 || !data.count}
                    className="btn-volt disabled:opacity-50"
                    data-testid="announce-review-button"
                  >
                    <Send size={14} /> Send to {data.count} Subscriber{data.count === 1 ? "" : "s"}
                  </button>
                ) : (
                  <div className="flex flex-wrap items-center gap-3" data-testid="announce-confirm-row">
                    <span className="font-mono2 text-[11px] uppercase tracking-wider text-volt">Send "{subject.trim()}" to {data.count}?</span>
                    <button onClick={sendAnnouncement} disabled={busy} className="btn-volt !py-2 !px-4 !text-sm disabled:opacity-60" data-testid="announce-confirm-button">
                      {busy ? "Sending…" : "Yes, Send Now"}
                    </button>
                    <button onClick={() => setConfirming(false)} className="btn-ghost !py-2 !px-4 !text-sm" data-testid="announce-cancel-button">Cancel</button>
                  </div>
                )}
              </div>
            </div>

            <div className="border border-white/10 bg-[#0c0c0c] p-6" data-testid="digest-log-card">
              <p className="eyebrow mb-4">Recent Sends</p>
              {digests.length === 0 ? (
                <p className="text-zinc-500 text-sm" data-testid="digest-log-empty">Nothing sent yet. The log fills in after the first digest.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm min-w-[520px]">
                    <thead>
                      <tr className="text-left font-mono2 text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                        <th className="pb-3 pr-4 font-normal">Subject</th>
                        <th className="pb-3 pr-4 font-normal">Type</th>
                        <th className="pb-3 pr-4 font-normal">Delivered</th>
                        <th className="pb-3 font-normal">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {digests.map((d) => (
                        <tr key={d.digest_id} className="border-t border-white/5" data-testid={`digest-row-${d.digest_id}`}>
                          <td className="py-3 pr-4 text-white">{d.subject}</td>
                          <td className="py-3 pr-4"><span className={`font-mono2 text-[10px] uppercase tracking-wider px-2 py-0.5 border ${d.type === "new_event" ? "border-volt/50 text-volt" : "border-white/20 text-zinc-300"}`}>{d.type === "new_event" ? "Auto · Event" : "Announcement"}</span></td>
                          <td className="py-3 pr-4 text-zinc-300">{d.sent}/{d.total}{d.failed ? <span className="text-red-400"> · {d.failed} failed</span> : ""}</td>
                          <td className="py-3 text-zinc-500">{fmt(d.created_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="border border-white/10 bg-[#0c0c0c] p-6" data-testid="subscribers-list-card">
              <p className="eyebrow mb-4">Subscriber List ({data.count})</p>
              {data.count === 0 ? (
                <p className="text-zinc-500 text-sm" data-testid="subscribers-empty">
                  No subscribers yet. The signup form sits mid-article on the pillar blog posts.
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm min-w-[480px]">
                    <thead>
                      <tr className="text-left font-mono2 text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                        <th className="pb-3 pr-4 font-normal">Email</th>
                        <th className="pb-3 pr-4 font-normal">Source</th>
                        <th className="pb-3 pr-4 font-normal">Joined</th>
                        <th className="pb-3 font-normal"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.subscribers.map((s) => (
                        <tr key={s.email} className="border-t border-white/5" data-testid={`subscriber-row-${s.email}`}>
                          <td className="py-3 pr-4 text-white">{s.email}</td>
                          <td className="py-3 pr-4 text-zinc-400 font-mono2 text-xs">{s.source || "—"}</td>
                          <td className="py-3 pr-4 text-zinc-500">{fmt(s.created_at)}</td>
                          <td className="py-3 text-right">
                            <button onClick={() => removeSubscriber(s.email)} className="text-zinc-600 hover:text-red-400 transition-colors" title="Remove" data-testid={`subscriber-remove-${s.email}`}>
                              <Trash2 size={14} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </AdminShell>
  );
}
